import { Form } from 'antd'
import { t } from 'i18next'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import {
  ModalBase,
  ModalContentContainer,
  ModalStepsTitle,
} from '../../../../../components'
import { optionalRequiredMark } from '../../../../../constants/constants'
import SmartFunctionActionDropdownDropDownItem from '../../../components/SmartFunctionActionDropdown/SmartFunctionActionDropdownDropDownItem'
import FlowCytometryReviewAnalysisConfigurationModalFooter from './footer/FlowCytometryReviewAnalysisConfigurationModalFooter'
import FlowCytometryReviewAnalysisConfigurationModalDataStep from './steps/FlowCytometryReviewAnalysisConfigurationModalDataStep'
import FlowCytometryReviewAnalysisConfigurationModalMappingStep from './steps/FlowCytometryReviewAnalysisConfigurationModalMappingStep'
import FlowCytometryReviewAnalysisConfigurationModalReviewStep from './steps/FlowCytometryReviewAnalysisConfigurationModalReviewStep'
import FlowCytometryReviewAnalysisConfigurationModalSampleStep, {
  FlowCytometryReviewAnalysisConfigurationFormValues,
} from './steps/FlowCytometryReviewAnalysisConfigurationModalStep'


const FlowCytometryReviewAnalysisConfigurationModal = () => {
  const [form] =
    Form.useForm<FlowCytometryReviewAnalysisConfigurationFormValues>()
  const [open, setOpen] = useState(false)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)

  const defaultValues: FlowCytometryReviewAnalysisConfigurationFormValues =
    useMemo(
      () => ({
        visitCode: '',
        tubeName: '',
        drugSpike: '',
        experimentId: '',
        additionalExperimentIds: '',
        ssrExperimentId: '',
      }),
      []
    )

  const resetValues = useCallback(() => {
    form.resetFields()
    setCurrentStep(0)
    setConfirmLoading(false)
  }, [form])

  const experimentId = Form.useWatch('experimentId', form) ?? ''
  const ssrExperimentId = Form.useWatch('ssrExperimentId', form) ?? ''
  const visitCode = Form.useWatch('visitCode', form) ?? ''

  const dataMissing = useMemo(() => {
    return !(experimentId ?? '').trim() || !(ssrExperimentId ?? '').trim()
  }, [experimentId, ssrExperimentId])

  const sampleMissing = useMemo(() => {
    return !(visitCode ?? '').trim()
  }, [visitCode])

  const experimentIdHasErrors =
    (form.getFieldError('experimentId') ?? []).length > 0
  const ssrIdHasErrors =
    (form.getFieldError('ssrExperimentId') ?? []).length > 0
  const initialSampleVisitCodeHasErrors =
    (form.getFieldError('visitCode') ?? []).length > 0
  const initialSampleStandardValueHasErrors = false
  const additionalSamplesHasErrors = false

  const submitDisabled = useMemo(() => {
    return (
      dataMissing ||
      sampleMissing ||
      experimentIdHasErrors ||
      ssrIdHasErrors ||
      initialSampleVisitCodeHasErrors ||
      initialSampleStandardValueHasErrors ||
      additionalSamplesHasErrors
    )
  }, [
    additionalSamplesHasErrors,
    dataMissing,
    experimentIdHasErrors,
    initialSampleStandardValueHasErrors,
    initialSampleVisitCodeHasErrors,
    sampleMissing,
    ssrIdHasErrors,
  ])

  const nextDisabled = useMemo(() => {
    if (currentStep === 0) {
      return dataMissing || experimentIdHasErrors || ssrIdHasErrors
    }
    if (currentStep === 1) {
      return (
        sampleMissing ||
        initialSampleVisitCodeHasErrors ||
        initialSampleStandardValueHasErrors ||
        additionalSamplesHasErrors
      )
    }
    if (currentStep === 2) return false
    return true
  }, [
    additionalSamplesHasErrors,
    currentStep,
    dataMissing,
    experimentIdHasErrors,
    initialSampleStandardValueHasErrors,
    initialSampleVisitCodeHasErrors,
    sampleMissing,
    ssrIdHasErrors,
  ])

  const handleOk = useCallback(() => {
    setConfirmLoading(true)
    form.submit()
  }, [form])

  const handleCancel = useCallback(() => {
    resetValues()
    setOpen(false)
  }, [resetValues])

  const handleNext = useCallback(() => {
    if (currentStep === 0) {
      form
        .validateFields(['experimentId', 'ssrExperimentId'])
        .then(() => setCurrentStep((currentStep) => currentStep + 1))
      return
    }
    if (currentStep === 1) {
      form
        .validateFields(['visitCode'])
        .then(() => setCurrentStep((currentStep) => currentStep + 1))
      return
    }
    if (currentStep === 2) {
      setCurrentStep((currentStep) => currentStep + 1)
    }
  }, [currentStep, form])

  const handlePrevious = useCallback(() => {
    setCurrentStep((currentStep) => Math.max(0, currentStep - 1))
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      resetValues()
      setOpen(false)
    } finally {
      setConfirmLoading(false)
    }
  }, [resetValues])

  const stepItems = useMemo(
    () => [
      { title: t('reviewAnalysisConfigurationModal.steps.data.title') },
      { title: t('reviewAnalysisConfigurationModal.steps.sample.title') },
      { title: t('reviewAnalysisConfigurationModal.steps.mapping.title') },
      { title: t('reviewAnalysisConfigurationModal.steps.review.title') },
    ],
    []
  )

  const steps: ReactNode[] = useMemo(
    () => [
      <FlowCytometryReviewAnalysisConfigurationModalDataStep />,
      <FlowCytometryReviewAnalysisConfigurationModalSampleStep />,
      <FlowCytometryReviewAnalysisConfigurationModalMappingStep />,
      <FlowCytometryReviewAnalysisConfigurationModalReviewStep
        form={form}
      />,
    ],
    [form]
  )

  const footers: ReactNode[] = useMemo(
    () => [
      <FlowCytometryReviewAnalysisConfigurationModalFooter
        onCancel={handleCancel}
        onNext={handleNext}
        nextDisabled={nextDisabled}
      />,
      <FlowCytometryReviewAnalysisConfigurationModalFooter
        onCancel={handleCancel}
        onPrevious={handlePrevious}
        onNext={handleNext}
        nextDisabled={nextDisabled}
      />,
      <FlowCytometryReviewAnalysisConfigurationModalFooter
        onCancel={handleCancel}
        onPrevious={handlePrevious}
        onNext={handleNext}
        nextDisabled={nextDisabled}
      />,
      <FlowCytometryReviewAnalysisConfigurationModalFooter
        onCancel={handleCancel}
        onPrevious={handlePrevious}
        onSubmit={handleOk}
        submitDisabled={submitDisabled}
        submitLoading={confirmLoading}
      />,
    ],
    [
      confirmLoading,
      handleCancel,
      handleNext,
      handleOk,
      handlePrevious,
      nextDisabled,
      submitDisabled,
    ]
  )

  return (
    <>
      <SmartFunctionActionDropdownDropDownItem
        onClick={() => {
          setOpen(true)
        }}
      >
        {t('workspaceDetailsPage.workspaceSmartFunctionPanel.actionMenu.configureRun')}
      </SmartFunctionActionDropdownDropDownItem>

      <ModalBase
        key="flow-cytometry-review-analysis-config"
        title={
          <ModalStepsTitle
            title={t('reviewAnalysisConfigurationModal.title')}
            currentStep={currentStep}
            stepItems={stepItems}
          />
        }
        open={open}
        onOk={handleOk}
        confirmLoading={confirmLoading}
        onCancel={handleCancel}
        destroyOnClose
        width={720}
        footer={footers[currentStep]}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 400px)',
            overflowX: 'hidden',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={defaultValues}
          requiredMark={optionalRequiredMark}
          onFinish={handleSubmit}
        >
          <ModalContentContainer>
            {steps[currentStep]}
          </ModalContentContainer>
        </Form>
      </ModalBase>
    </>
  )
}

export default FlowCytometryReviewAnalysisConfigurationModal

